/**
 * Hook per leggere lo stato della subscription dell'organizzazione corrente.
 *
 * La tabella `subscriptions` è popolata automaticamente per ogni org
 * (trial 7gg al primo signup). Il Lemon Squeezy webhook (TBD) aggiornerà
 * plan_code, status, current_period_end quando billing è attivo.
 *
 * Hook complementare a useOrgPlan (che legge il vecchio campo testuale
 * `organizations.plan` per il feature gating). Quando Lemon Squeezy sarà
 * attivo, `useOrgPlan` può essere refactorato per leggere da qui.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';
export type PlanCode = 'trial' | 'solo' | 'studio' | 'team';

export interface Subscription {
  id: string;
  organization_id: string;
  plan_code: PlanCode;
  status: SubscriptionStatus;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  lemonsqueezy_subscription_id: string | null;
  lemonsqueezy_customer_id: string | null;
  lemonsqueezy_variant_id: string | null;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionInfo {
  subscription: Subscription | null;
  isTrialing: boolean;
  isActive: boolean;
  /** Giorni rimasti al trial (clamp a 0). null se non in trial. */
  trialDaysLeft: number | null;
  /** Giorni alla scadenza del period corrente. null se non valorizzato. */
  daysUntilRenewal: number | null;
}

function daysBetween(future: string | null): number | null {
  if (!future) return null;
  const ms = new Date(future).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export function useSubscription(): SubscriptionInfo & { isLoading: boolean } {
  const { currentOrganizationId } = useCurrentOrganization();

  const query = useQuery<Subscription | null>({
    queryKey: ['subscription', currentOrganizationId],
    enabled: !!currentOrganizationId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!currentOrganizationId) return null;
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('subscriptions' as any)
        .select('*')
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();
      if (error) {
        console.error('[useSubscription] error:', error);
        return null;
      }
      return (data as unknown as Subscription) ?? null;
    },
  });

  const sub = query.data ?? null;
  const isTrialing = sub?.status === 'trialing';
  const isActive = sub?.status === 'active' || sub?.status === 'trialing';
  const trialDaysLeft = isTrialing ? daysBetween(sub?.trial_ends_at ?? null) : null;
  const daysUntilRenewal = daysBetween(sub?.current_period_end ?? null);

  return {
    subscription: sub,
    isTrialing,
    isActive,
    trialDaysLeft,
    daysUntilRenewal,
    isLoading: query.isLoading,
  };
}
